import { useMemo, useState } from 'react';
import type { CSSProperties, MouseEvent } from 'react';
import CollapseToggle from '@/components/tabletop/components/CollapseToggle';
import CardBack from '@/components/card/CardBack';
import { HandCardState } from '@/types/game';
import { getHandCardView } from '@/utils/handView';
import { getPlayerColor } from '@/utils/playerColors';
import { selectionGlowFilter } from '@/utils/colorTransforms';

type CardWidthStrategy = 'adaptive' | 'fixed';

export interface HandPanelProps {
  cards: HandCardState[];
  viewerId?: string | null;
  playerNames?: Record<string, string>;
  title?: string;
  emptyStateMessage?: string;
  isHighlighted?: boolean;
  collapsed?: boolean;
  defaultCollapsed?: boolean;
  onCollapseChange?: (collapsed: boolean) => void;
  onCardMouseDown?: (cardId: string, event: MouseEvent<HTMLDivElement>) => void;
  onCardMouseEnter?: (cardId: string) => void;
  onCardMouseLeave?: (cardId: string) => void;
  onFlipCard?: (cardId: string) => void;
  onSelectCard?: (cardId: string) => void;
  selectedCardId?: string | null;
  allowDrag?: boolean;
  allowFlip?: boolean;
  allowSelect?: boolean;
  cardWidthStrategy?: CardWidthStrategy;
  cardWidth?: number;
  adaptiveCardMinWidth?: number;
  collapsedCardWidth?: number;
  className?: string;
  selectionColor?: string | null;
  cardFaceRotation?: number;
}

const DEFAULT_CARD_WIDTH = 96;
const DEFAULT_MIN_CARD_WIDTH = 60;
const DEFAULT_COLLAPSED_CARD_WIDTH = 48;
const CARD_ASPECT_RATIO = 88 / 63;

export default function HandPanel({
  cards,
  viewerId,
  playerNames,
  title = 'Hand',
  emptyStateMessage = 'Draw cards from a deck to populate this hand.',
  isHighlighted = false,
  collapsed,
  defaultCollapsed = false,
  onCollapseChange,
  onCardMouseDown,
  onCardMouseEnter,
  onCardMouseLeave,
  onFlipCard,
  onSelectCard,
  selectedCardId,
  allowDrag = true,
  allowFlip = true,
  allowSelect = true,
  cardWidthStrategy = 'adaptive',
  cardWidth,
  adaptiveCardMinWidth = DEFAULT_MIN_CARD_WIDTH,
  collapsedCardWidth = DEFAULT_COLLAPSED_CARD_WIDTH,
  className = '',
  selectionColor,
  cardFaceRotation = 0
}: HandPanelProps) {
  const [internalCollapsed, setInternalCollapsed] = useState(defaultCollapsed);
  const isCollapsed = collapsed ?? internalCollapsed;

  const setCollapsed = (next: boolean) => {
    if (collapsed === undefined) {
      setInternalCollapsed(next);
    }
    onCollapseChange?.(next);
  };

  const handleToggle = () => {
    setCollapsed(!isCollapsed);
  };

  const localPlayerColor = useMemo(() => (viewerId ? getPlayerColor(viewerId) : null), [viewerId]);

  const effectiveSelectionColor = selectionColor === undefined
    ? localPlayerColor?.hex ?? null
    : selectionColor;

  const effectiveCardWidth = useMemo(() => {
    if (cardWidthStrategy === 'fixed' && cardWidth) {
      return cardWidth;
    }

    const base = cardWidth ?? DEFAULT_CARD_WIDTH;
    if (cards.length <= 7) return base;
    const adjusted = base - (cards.length - 7) * 6;
    return Math.max(adaptiveCardMinWidth, adjusted);
  }, [cardWidthStrategy, cardWidth, cards.length, adaptiveCardMinWidth]);

  const effectiveCollapsedCardWidth = collapsedCardWidth;

  const cardHeight = useMemo(
    () => Math.round(effectiveCardWidth * CARD_ASPECT_RATIO),
    [effectiveCardWidth]
  );
  const collapsedCardHeight = useMemo(
    () => Math.round(effectiveCollapsedCardWidth * CARD_ASPECT_RATIO),
    [effectiveCollapsedCardWidth]
  );

  const handPanelClasses = useMemo(() => {
    const classes = [
      'table-panel-strong',
      'transition-all',
      'duration-200'
    ];
    if (isHighlighted) {
      classes.push('ring-2', 'ring-emerald-400/70', 'scale-[1.01]', 'shadow-[0_0_0_1px_rgba(16,185,129,0.2),0_24px_50px_rgba(16,185,129,0.16)]');
    }
    return classes.join(' ');
  }, [isHighlighted]);

  const cardViews = useMemo(() => {
    return cards.map(card => {
      const view = getHandCardView(card, viewerId);
      const isSelectedLocally = selectedCardId === card.id;
      const isSelectedByOthers = view.isSelectedByOthers;
      const otherSelectorNames = view.otherSelectors.map(id => playerNames?.[id] ?? 'Opponent');
      const firstOtherSelectorColor = view.otherSelectors.length > 0 ? getPlayerColor(view.otherSelectors[0]) : null;

      const tooltipParts: string[] = [];
      if (view.showFrontFace && card.name) {
        tooltipParts.push(card.name);
      }
      if (view.isOwner && view.isRevealedToOthers) {
        tooltipParts.push('Shown to opponents');
      }
      if (!view.isOwner && view.isRevealedToOthers) {
        tooltipParts.push('Revealed');
      }
      if (otherSelectorNames.length > 0) {
        tooltipParts.push(`Selected by ${otherSelectorNames.join(', ')}`);
      }

      return {
        card,
        view,
        isSelectedLocally,
        isSelectedByOthers,
        otherSelectorNames,
        firstOtherSelectorColor,
        tooltip: tooltipParts.join(' · ') || 'Hand card'
      };
    });
  }, [cards, viewerId, playerNames, selectedCardId]);

  return (
    <div className={`${handPanelClasses} ${className}`}>
      <div className="flex items-start justify-between gap-2 px-3 py-2.5 sm:items-center sm:gap-3 sm:px-4">
        <div className="flex min-w-0 flex-1 items-center gap-2 sm:gap-3">
          <div className="min-w-0">
            <h3 className="truncate text-sm font-semibold text-slate-900 dark:text-slate-100">{title}</h3>
            <p className="whitespace-nowrap text-[11px] uppercase tracking-[0.18em] text-slate-500 dark:text-slate-400">
              {cards.length === 0 ? 'Empty' : `${cards.length} card${cards.length === 1 ? '' : 's'}`}
            </p>
          </div>
          {isCollapsed && cardViews.length > 0 && (
            <div className="flex min-w-0 flex-1 items-center justify-end overflow-hidden">
              <div className="max-w-full overflow-x-auto overscroll-x-none px-1 py-1 sm:px-2">
                <div className="flex min-w-full justify-start">
                  <div className="flex items-end gap-2">
                    {cardViews.map(
                      ({
                        card,
                        view,
                        isSelectedByOthers,
                        isSelectedLocally,
                        firstOtherSelectorColor,
                        tooltip
                      }) => (
                        <div
                          key={`collapsed-${card.id}`}
                          data-hand-card-id={card.id}
                          className="relative flex-shrink-0 transition-transform duration-150 hover:-translate-y-0.5"
                          style={{
                            width: `${effectiveCollapsedCardWidth}px`,
                            height: `${collapsedCardHeight}px`
                          }}
                          onMouseDown={event => {
                            if (!allowDrag) return;
                            if (event.button !== 0) return;
                            event.stopPropagation();
                            onCardMouseDown?.(card.id, event);
                          }}
                          onMouseEnter={() => onCardMouseEnter?.(card.id)}
                          onMouseLeave={() => onCardMouseLeave?.(card.id)}
                          onClick={event => {
                            if (!allowSelect) return;
                            event.stopPropagation();
                            onSelectCard?.(card.id);
                          }}
                          onContextMenu={event => {
                            if (!allowFlip || !onFlipCard) return;
                            if (!view.isOwner) return;
                            event.preventDefault();
                            event.stopPropagation();
                            onFlipCard(card.id);
                          }}
                        >
                          <div
                            className="h-full w-full overflow-hidden rounded-sm bg-slate-200 shadow-sm dark:bg-slate-800"
                            style={{ transform: `rotate(${cardFaceRotation}deg)` }}
                          >
                            {view.showFrontFace && view.displayImageUrl ? (
                              <img
                                src={view.displayImageUrl}
                                alt={card.name || 'Card'}
                                className="h-full w-full select-none object-cover"
                                draggable={false}
                                loading="lazy"
                              />
                            ) : (
                              <CardBack backImageUrl={view.backImageUrl} className="h-full w-full" />
                            )}
                          </div>

                          {isSelectedByOthers && firstOtherSelectorColor && (
                            <span
                              className="pointer-events-none absolute inset-0 rounded-sm ring-2 ring-offset-1 ring-offset-transparent dark:ring-offset-transparent"
                              style={{
                                '--tw-ring-color': firstOtherSelectorColor.hex,
                                '--tw-ring-opacity': 1,
                                filter: selectionGlowFilter(firstOtherSelectorColor.hex, {
                                  blur: 12,
                                  alpha: 0.5,
                                  saturation: 1.2
                                })
                              } as CSSProperties}
                            />
                          )}

                          {isSelectedLocally && (
                            <span
                              className={`pointer-events-none absolute inset-0 rounded-sm ring-2 ring-offset-1 ring-offset-transparent dark:ring-offset-transparent ${
                                effectiveSelectionColor
                                  ? ''
                                  : 'ring-indigo-300/90 dark:ring-indigo-400/80 saturate-125'
                              }`}
                              style={
                                effectiveSelectionColor
                                  ? ({
                                      '--tw-ring-color': effectiveSelectionColor,
                                      '--tw-ring-opacity': 1,
                                      filter:
                                        selectionGlowFilter(effectiveSelectionColor, {
                                          blur: 12,
                                          alpha: 0.5,
                                          saturation: 1.2
                                        }) ??
                                        'saturate(1.2) drop-shadow(0 0 12px rgba(129, 140, 248, 0.45))'
                                    } as CSSProperties)
                                  : ({ filter: 'saturate(1.2) drop-shadow(0 0 12px rgba(129, 140, 248, 0.45))' } as CSSProperties)
                              }
                            />
                          )}
                        </div>
                      )
                    )}
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
        <CollapseToggle collapsed={isCollapsed} onToggle={handleToggle} label="Toggle hand" className="shrink-0" />
      </div>

      {!isCollapsed && (
        <div className="px-3 pb-3 sm:px-4">
          {cards.length === 0 ? (
            <div className="flex h-14 items-center justify-center rounded-xl border border-dashed border-slate-900/10 bg-white/30 px-5 text-center text-xs text-slate-500 dark:border-white/10 dark:bg-white/4 dark:text-slate-400 sm:h-16">
              {emptyStateMessage}
            </div>
          ) : (
            <div className="overflow-x-auto overscroll-x-none px-1 pb-2 pt-2">
              <div className="flex min-w-full justify-start">
                <div className="flex items-end gap-3">
                  {cardViews.map(
                    ({
                      card,
                      view,
                      isSelectedByOthers,
                      isSelectedLocally,
                      otherSelectorNames,
                      firstOtherSelectorColor,
                      tooltip
                    }) => (
                      <div
                        key={card.id}
                        data-hand-card-id={card.id}
                        className="relative flex-shrink-0 transition-transform duration-150 hover:-translate-y-1"
                        style={{ width: `${effectiveCardWidth}px`, height: `${cardHeight}px` }}
                        onMouseDown={event => {
                          if (!allowDrag) return;
                          if (event.button !== 0) return;
                          event.stopPropagation();
                          onCardMouseDown?.(card.id, event);
                        }}
                        onMouseEnter={() => onCardMouseEnter?.(card.id)}
                        onMouseLeave={() => onCardMouseLeave?.(card.id)}
                        onClick={event => {
                          if (!allowSelect) return;
                          event.stopPropagation();
                          onSelectCard?.(card.id);
                        }}
                        onContextMenu={event => {
                          if (!allowFlip || !onFlipCard) return;
                          if (!view.isOwner) return;
                          event.preventDefault();
                          event.stopPropagation();
                          onFlipCard(card.id);
                        }}
                      >
                        <div
                          className="h-full w-full overflow-hidden rounded-md bg-slate-200 shadow-md dark:bg-slate-800"
                          style={{ transform: `rotate(${cardFaceRotation}deg)` }}
                        >
                          {view.showFrontFace && view.displayImageUrl ? (
                            <img
                              src={view.displayImageUrl}
                              alt={card.name || 'Card'}
                              className="h-full w-full select-none object-cover"
                              draggable={false}
                              loading="lazy"
                            />
                          ) : (
                            <CardBack backImageUrl={view.backImageUrl} className="h-full w-full" />
                          )}
                        </div>

                        {isSelectedByOthers && firstOtherSelectorColor && (
                          <span
                            className="pointer-events-none absolute inset-0 rounded-md ring-4 ring-offset-2 ring-offset-transparent dark:ring-offset-transparent transition-opacity duration-150 opacity-100"
                            style={{
                              '--tw-ring-color': firstOtherSelectorColor.hex,
                              '--tw-ring-opacity': 1,
                              filter: selectionGlowFilter(firstOtherSelectorColor.hex, {
                                blur: 18,
                                alpha: 0.55,
                                saturation: 1.25
                              })
                            } as CSSProperties}
                          />
                        )}

                        {isSelectedLocally && (
                          <span
                            className={`pointer-events-none absolute inset-0 rounded-md ring-4 ring-offset-2 ring-offset-transparent dark:ring-offset-transparent transition-opacity duration-150 opacity-100 ${
                              effectiveSelectionColor
                                ? ''
                                : 'ring-indigo-300/90 dark:ring-indigo-400/80 saturate-125'
                            }`}
                            style={
                              effectiveSelectionColor
                                ? ({
                                    '--tw-ring-color': effectiveSelectionColor,
                                    '--tw-ring-opacity': 1,
                                    filter:
                                      selectionGlowFilter(effectiveSelectionColor, {
                                        blur: 18,
                                        alpha: 0.55,
                                        saturation: 1.25
                                      }) ??
                                      'saturate(1.25) drop-shadow(0 0 16px rgba(129, 140, 248, 0.45))'
                                  } as CSSProperties)
                                : ({ filter: 'saturate(1.25) drop-shadow(0 0 16px rgba(129, 140, 248, 0.45))' } as CSSProperties)
                            }
                          />
                        )}

                        {view.isOwner && view.isRevealedToOthers && (
                          <span className="absolute top-1 left-1 rounded-full bg-amber-500/90 px-2 py-0.5 text-[10px] font-semibold text-white shadow">
                            Shown
                          </span>
                        )}

                        {isSelectedByOthers && firstOtherSelectorColor && (
                          <span
                            className="pointer-events-none absolute -top-1 -right-1 inline-flex h-5 min-w-[1.25rem] items-center justify-center rounded-full px-1 text-[10px] font-semibold text-white shadow transition-opacity duration-150 opacity-100"
                            style={{ backgroundColor: firstOtherSelectorColor.hex }}
                          >
                            {view.otherSelectors.length > 9 ? '9+' : view.otherSelectors.length || ''}
                          </span>
                        )}
                      </div>
                    )
                  )}
                </div>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
